import { FunctionComponent, ReactNode, useRef, useState } from "react";
import buttonStyles from "./styles/PaginationButtonAtom.module.css";
import pageStyles from "./styles/PaginationPageAtom.module.css";
import bulletStyles from "./styles/PaginationBulletAtom.module.css";
import { StandalonePlaceholderRegularIcon } from "@deriv/quill-icons";

import { useFocusHandling } from "../../hooks/useFocusHandling";

const paginationAtomStyles = {
  button: buttonStyles,
  page: pageStyles,
  bullet: bulletStyles,
} as const;

export type PaginationButtonAtomType = {
  className?: string;
  label?: string | ReactNode;
  /** Variant props */
  state?: "default" | "disabled" | "selected";

  /** Custom props */
  variant?: "button" | "page" | "bullet";
  icon?: ReactNode;
  isSelected?: boolean;
  onClick?: () => void;
};

const PaginationAtom: FunctionComponent<PaginationButtonAtomType> = ({
  className = "",
  state = "default",
  variant = "button",
  icon,
  label = "0",
  onClick,
}) => {
  const styles = paginationAtomStyles[variant];
  const paginationAtomId = `pagination-atom-${variant}-${state}`;
  const IconComponent = icon || (
    <StandalonePlaceholderRegularIcon iconSize="md" />
  );

  const buttonRef = useRef<HTMLDivElement>(null);
  const [highlight, setHighlight] = useState(false);

  const handleClick = () => {
    if (state === "disabled") return;
    onClick?.();
  };

  const { handleFocus, handleBlur } = useFocusHandling({
    isDisabled: state === "disabled",
    setHighlight,
    onEnter: () => {
      handleClick();
    },
  });

  return (
    <div
      ref={buttonRef}
      className={[
        styles.root,
        "quill-ui-next-atom",
        "quill-ui-next-no-select",
        state !== "disabled" && "quill-ui-next-pointer",
        className,
      ].join(" ")}
      data-id={paginationAtomId}
      data-state={state}
      tabIndex={state !== "disabled" ? 0 : -1}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={handleClick}
    >
      {variant === "button" && (
        <>
          <div className={styles.customIcon}>{IconComponent}</div>
          <div className={styles.hiddenLabel}></div>
        </>
      )}

      {variant === "page" && <div className={styles.label}>{label}</div>}

      {highlight && <div className={styles.focus} />}
    </div>
  );
};

PaginationAtom.displayName = "PaginationAtom";

export default PaginationAtom;
